import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, ExternalLink, Copy, ArrowRight, ArrowLeft, Sparkles, Mail } from "lucide-react";

const STEPS = [
  { id: "key", title: "API Key" },
  { id: "domain", title: "Add Domain" },
  { id: "dns", title: "DNS Records" },
  { id: "sender", title: "Sender Identity" },
  { id: "test", title: "Send Test" },
];

function StepHeader({ current }) {
  return (
    <div className="flex items-center justify-between mb-6 overflow-x-auto pb-2">
      {STEPS.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={s.id} className="flex items-center shrink-0">
            <div className={`flex items-center gap-2 ${active ? "text-deep-blue" : done ? "text-green-600" : "text-slate-400"}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${active ? "bg-deep-blue text-white" : done ? "bg-green-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                {done ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-xs font-medium hidden sm:inline ${active ? "text-deep-blue" : done ? "text-green-600" : "text-slate-400"}`}>{s.title}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`w-6 sm:w-10 h-px mx-2 ${done ? "bg-green-300" : "bg-slate-200"}`} />}
          </div>
        );
      })}
    </div>
  );
}

function CopyField({ value, label }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); toast.success(`${label} copied`); }
    catch { toast.error("Clipboard unavailable — copy manually"); }
  };
  return (
    <button type="button" onClick={copy} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-2 py-1 hover:bg-slate-100 transition-colors w-full text-left" data-testid={`copy-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <code className="text-xs flex-1 truncate text-slate-700">{value}</code>
      <Copy className="w-3 h-3 text-slate-400 shrink-0" />
    </button>
  );
}

export default function ResendWizard({ open, onOpenChange, onComplete }) {
  const [step, setStep] = useState(0);
  // Step 0 — API Key
  const [apiKey, setApiKey] = useState("");
  const [keyValidating, setKeyValidating] = useState(false);
  const [keyResult, setKeyResult] = useState(null); // {ok, detail}
  const [keySaved, setKeySaved] = useState(false);

  // Step 1 — Domain
  const [domain, setDomain] = useState("bluevsblue.org");
  const [addingDomain, setAddingDomain] = useState(false);
  const [domainData, setDomainData] = useState(null); // resend domain object

  // Step 2 — DNS verification (auto-poll)
  const [verifying, setVerifying] = useState(false);
  const pollRef = useRef(null);

  // Step 3 — Sender
  const [senderEmail, setSenderEmail] = useState("");
  const [senderName, setSenderName] = useState("Blue vs Blue");
  const [savingSender, setSavingSender] = useState(false);

  // Step 4 — Test
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Pre-load existing settings when opened
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/settings/admin`, { withCredentials: true });
        if (res.data.resend_api_key) {
          setApiKey(res.data.resend_api_key);
          setKeySaved(true);
          setKeyResult({ ok: true, detail: "Existing API key detected — validate to continue." });
        }
        if (res.data.sender_email) {
          setSenderEmail(res.data.sender_email);
          const guessedDomain = res.data.sender_email.split("@")[1];
          if (guessedDomain) setDomain(guessedDomain);
        }
        if (res.data.sender_name) setSenderName(res.data.sender_name);
      } catch {}
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open]);

  const validateAndSaveKey = async () => {
    if (!apiKey.trim()) return;
    setKeyValidating(true);
    setKeyResult(null);
    try {
      const v = await axios.post(`${API}/settings/resend/validate-key`, { api_key: apiKey.trim() }, { withCredentials: true });
      setKeyResult(v.data);
      if (v.data.ok) {
        await axios.post(`${API}/settings/resend/save-key`, { api_key: apiKey.trim(), sender_name: senderName }, { withCredentials: true });
        setKeySaved(true);
      }
    } catch (err) {
      setKeyResult({ ok: false, detail: err.response?.data?.detail || "Validation failed" });
    } finally { setKeyValidating(false); }
  };

  const addDomainStep = async () => {
    if (!domain.trim()) return;
    setAddingDomain(true);
    try {
      const res = await axios.post(`${API}/settings/resend/add-domain`, { domain: domain.trim() }, { withCredentials: true });
      setDomainData(res.data.domain);
      if (res.data.already_existed) toast.success("Domain was already in your Resend account");
      else toast.success("Domain added to Resend");
      setStep(2);
      // Auto-start polling
      startVerificationPoll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add domain");
    } finally { setAddingDomain(false); }
  };

  const triggerVerify = async () => {
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/settings/resend/verify-domain`, { domain }, { withCredentials: true });
      setDomainData(res.data.domain);
      const status = (res.data.domain?.status || "").toLowerCase();
      if (status === "verified") {
        toast.success("Domain verified! 🎉");
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      } else {
        toast.info(`Status: ${status || "pending"} — DNS records may still be propagating.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally { setVerifying(false); }
  };

  const startVerificationPoll = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/settings/resend/domain-status?domain=${encodeURIComponent(domain)}`, { withCredentials: true });
        setDomainData(res.data.domain);
        if ((res.data.domain?.status || "").toLowerCase() === "verified") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          toast.success("Domain verified! 🎉");
        }
      } catch {}
    }, 15000);
  };

  const saveSender = async () => {
    if (!senderEmail.trim() || !senderEmail.includes("@")) { toast.error("Enter a valid sender email"); return; }
    setSavingSender(true);
    try {
      await axios.post(`${API}/settings/resend/save-key`, { api_key: apiKey, sender_email: senderEmail.trim().toLowerCase(), sender_name: senderName }, { withCredentials: true });
      toast.success("Sender identity saved");
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSavingSender(false); }
  };

  const sendTest = async () => {
    setSendingTest(true); setTestResult(null);
    try {
      const res = await axios.post(`${API}/settings/test-email`, {}, { withCredentials: true });
      setTestResult({ ok: true, message: `Test email sent to ${res.data.sent_to}` });
      toast.success("Sent! Check your inbox (and spam folder).");
    } catch (err) {
      setTestResult({ ok: false, message: err.response?.data?.detail || "Send failed" });
    } finally { setSendingTest(false); }
  };

  const finish = () => {
    onOpenChange(false);
    if (onComplete) onComplete();
  };

  // Resend's "records" array contains the DNS records to add
  const dnsRecords = domainData?.records || [];
  const domainStatus = (domainData?.status || "").toLowerCase();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="resend-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-deep-blue" />
            Resend Email Setup Wizard
          </DialogTitle>
        </DialogHeader>

        <StepHeader current={step} />

        {/* === STEP 0: API KEY === */}
        {step === 0 && (
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-deep-blue mb-1">Step 1 — Paste your Resend API key</h3>
              <p className="text-sm text-slate-600">Don't have one yet? Sign up free at{" "}
                <a href="https://resend.com/signup" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">resend.com/signup <ExternalLink className="w-3 h-3" /></a>{" "}
                then create a key at{" "}
                <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">API Keys <ExternalLink className="w-3 h-3" /></a>.
              </p>
            </div>
            <div>
              <Label htmlFor="wizard-key">API Key (starts with re_)</Label>
              <Input
                id="wizard-key"
                type="password"
                value={apiKey}
                onChange={(e) => { setApiKey(e.target.value); setKeyResult(null); setKeySaved(false); }}
                placeholder="re_PrwBb7zs_..."
                data-testid="wizard-key-input"
              />
            </div>
            {keyResult && (
              <div className={`flex items-start gap-2 text-sm p-3 rounded ${keyResult.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}>
                {keyResult.ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 shrink-0 mt-0.5" />}
                <span>{keyResult.detail}</span>
              </div>
            )}
            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <div className="flex gap-2">
                <Button onClick={validateAndSaveKey} disabled={!apiKey.trim() || keyValidating} variant="outline" data-testid="wizard-validate-key">
                  {keyValidating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Validate &amp; Save
                </Button>
                <Button onClick={() => setStep(1)} disabled={!keySaved || !keyResult?.ok} className="bg-deep-blue text-white" data-testid="wizard-next-1">
                  Next <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* === STEP 1: DOMAIN === */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-deep-blue mb-1">Step 2 — Add your sending domain</h3>
              <p className="text-sm text-slate-600">We'll register this domain with Resend. You'll add the DNS records they generate in the next step.</p>
            </div>
            <div>
              <Label htmlFor="wizard-domain">Domain</Label>
              <Input
                id="wizard-domain"
                value={domain}
                onChange={(e) => setDomain(e.target.value.toLowerCase())}
                placeholder="bluevsblue.org"
                data-testid="wizard-domain-input"
              />
              <p className="text-xs text-slate-500 mt-1">Use the bare domain (no <code>www.</code>, no <code>https://</code>).</p>
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(0)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button onClick={addDomainStep} disabled={!domain.trim() || addingDomain} className="bg-deep-blue text-white" data-testid="wizard-add-domain">
                {addingDomain ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Add to Resend &amp; Continue <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* === STEP 2: DNS RECORDS === */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-deep-blue mb-1">Step 3 — Add these DNS records at your domain registrar</h3>
              <p className="text-sm text-slate-600">
                Log into your registrar (GoDaddy / Namecheap / Cloudflare / etc.), open the DNS settings for <strong>{domain}</strong>, and add each record below. Click the values to copy.
              </p>
            </div>

            {dnsRecords.length === 0 && (
              <p className="text-sm text-slate-500 italic">Loading DNS records...</p>
            )}

            <div className="space-y-3">
              {dnsRecords.map((rec, idx) => {
                const recStatus = (rec.status || "").toLowerCase();
                const ok = recStatus === "verified";
                return (
                  <div key={idx} className={`border rounded-lg p-3 ${ok ? "bg-green-50 border-green-200" : "bg-slate-50 border-slate-200"}`} data-testid={`dns-record-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-600">{rec.type} Record</span>
                      {ok
                        ? <span className="text-xs flex items-center gap-1 text-green-700 font-semibold"><CheckCircle2 className="w-3.5 h-3.5" /> Verified</span>
                        : <span className="text-xs text-amber-700 font-semibold">{recStatus || "pending"}</span>}
                    </div>
                    <div className="grid grid-cols-[80px_1fr] gap-x-2 gap-y-1.5 text-xs">
                      <span className="text-slate-500 font-medium pt-1">Name</span>
                      <CopyField value={rec.name} label={`${rec.type}-name-${idx}`} />
                      <span className="text-slate-500 font-medium pt-1">Value</span>
                      <CopyField value={rec.value || rec.record || ""} label={`${rec.type}-value-${idx}`} />
                      {rec.ttl && (<><span className="text-slate-500 font-medium">TTL</span><span className="text-slate-700">{rec.ttl}</span></>)}
                      {rec.priority !== undefined && rec.priority !== null && (<><span className="text-slate-500 font-medium">Priority</span><span className="text-slate-700">{rec.priority}</span></>)}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={`flex items-center gap-2 p-3 rounded ${domainStatus === "verified" ? "bg-green-100 text-green-800" : "bg-amber-50 text-amber-800"}`}>
              {domainStatus === "verified"
                ? <><CheckCircle2 className="w-5 h-5 shrink-0" /><span className="text-sm font-medium">All records verified — your domain is live!</span></>
                : <><Loader2 className="w-4 h-4 shrink-0 animate-spin" /><span className="text-sm">DNS records typically take 5–60 minutes to propagate. We'll auto-check every 15 seconds.</span></>}
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <div className="flex gap-2">
                <Button onClick={triggerVerify} disabled={verifying} variant="outline" data-testid="wizard-verify-now">
                  {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Verify Now
                </Button>
                <Button onClick={() => setStep(3)} disabled={domainStatus !== "verified"} className="bg-deep-blue text-white" data-testid="wizard-next-3">
                  Next <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* === STEP 3: SENDER === */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-deep-blue mb-1">Step 4 — Pick your sender identity</h3>
              <p className="text-sm text-slate-600">This is what recipients see in the "From" line of every transactional email.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="wizard-sender-email">Sender Email *</Label>
                <Input id="wizard-sender-email" type="email" value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)} placeholder={`info@${domain}`} data-testid="wizard-sender-email" />
                <p className="text-xs text-slate-500 mt-1">Must be on your verified domain (<strong>{domain}</strong>).</p>
              </div>
              <div>
                <Label htmlFor="wizard-sender-name">Sender Name</Label>
                <Input id="wizard-sender-name" value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="Blue vs Blue" data-testid="wizard-sender-name" />
                <p className="text-xs text-slate-500 mt-1">Display name shown to recipients.</p>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm">
              <p className="text-slate-500 text-xs mb-1">Preview:</p>
              <p className="font-mono text-deep-blue">{senderName} &lt;{senderEmail || `info@${domain}`}&gt;</p>
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button onClick={saveSender} disabled={savingSender || !senderEmail.includes("@")} className="bg-deep-blue text-white" data-testid="wizard-save-sender">
                {savingSender ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save &amp; Continue <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* === STEP 4: TEST === */}
        {step === 4 && (
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-deep-blue mb-1">Step 5 — Send yourself a test email</h3>
              <p className="text-sm text-slate-600">Final check — we'll send a real branded email to your admin address.</p>
            </div>
            <div className="flex justify-center">
              <Button onClick={sendTest} disabled={sendingTest} className="bg-deep-blue text-white" size="lg" data-testid="wizard-send-test">
                {sendingTest ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Mail className="w-5 h-5 mr-2" />}
                Send Test Email
              </Button>
            </div>
            {testResult && (
              <div className={`flex items-start gap-2 text-sm p-3 rounded ${testResult.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`} data-testid="wizard-test-result">
                {testResult.ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 shrink-0 mt-0.5" />}
                <span>{testResult.message}</span>
              </div>
            )}
            {testResult?.ok && (
              <div className="bg-green-50 border border-green-200 rounded p-4 text-sm text-green-900">
                <p className="font-semibold">🎉 You're done!</p>
                <p className="mt-1">Ticket purchase emails, donation receipts, conference invitations, and the weekly digest will all deliver automatically.</p>
              </div>
            )}
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(3)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button onClick={finish} className="bg-green-600 text-white hover:bg-green-700" data-testid="wizard-finish">
                {testResult?.ok ? "Finish" : "Close Wizard"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
